import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { base64ToFile, getBase64 } from "@/helper/helper";
import { useUpdateSystemSettings } from "@/hooks/useUpdateSystemSettings";
import { DocumentFile } from "@/types/types";
import { toast } from "react-toastify";
import useHeaderChangeNoUP from "@/hooks/useHeaderChangeNoUP";
import { useAppSelector } from "@/hooks/useAppSelector";

const useAdminConsole = () => {
  useHeaderChangeNoUP("Admin Console");

  const { colorScheme, logoFile } = useAppSelector((state) => state.system);
  const fileBlob = base64ToFile(logoFile?.fileValue, logoFile?.contentType);
  const defaultLogo = fileBlob
    ? new File([fileBlob], logoFile?.name ?? "logo.png", { type: logoFile?.contentType })
    : null;

  const [primaryColor, setPrimaryColor] = useState(colorScheme?.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(colorScheme?.secondaryColor);
  const [logo, setLogo] = useState<File | null>(defaultLogo);
  const qc = useQueryClient();

  const { mutateAsync, isPending, isSuccess, data } = useUpdateSystemSettings();

  useEffect(() => {
    if (isSuccess && !data?.validation?.items?.length) {
      toast.success("Settings updated successfully");
    }
  }, [isSuccess, data?.validation?.items?.length]);

  const handleSubmit = async () => {
    const updatedSettings = {
      colorScheme: {
        primaryColor,
        secondaryColor,
      },
      logo: logo
        ? ({
            fileValue: await getBase64(logo),
            name: logo.name,
            contentType: logo.type,
          } as Partial<DocumentFile>)
        : null,
    };
    await mutateAsync(updatedSettings);
    qc.invalidateQueries({ queryKey: ["user"] });
  };

  const handleRestoreDefaults = async () => {
    const updatedSettings = {
      colorScheme: null,
      logo: null,
    };
    const { data } = await mutateAsync(updatedSettings);
    const colorScheme = data?.colorScheme;

    await qc.invalidateQueries({ queryKey: ["user"] });
    setPrimaryColor(colorScheme?.primaryColor ?? "");
    setSecondaryColor(colorScheme?.secondaryColor ?? "");
    setLogo(null);
  };

  return {
    primaryColor,
    setPrimaryColor,
    secondaryColor,
    setSecondaryColor,
    logo,
    setLogo,
    handleSubmit,
    handleRestoreDefaults,
    isPending,
  };
};

export default useAdminConsole;
